<template>
    <aside class="el-popover-wrapper"
        :class="[config && config.focus ? 'el--focus' : '', ]"
        :style="{ display: config === false ? 'none' : 'block' }">
        <article class="f fv / el-popover"
            ref="popover"
            tabindex="-1"
            :role="config.comp !== false ? 'dialog' : null"
            :class="[
                config.class ? config.class : '',
                !config.nopad ? 'sp-s' : '',
                config.mobile_full ? 'el-popover--mfull' : '',
                `el-popoverIndex-${popoverIndex}`
            ]"
            v-mead-move="config.moveable"
            :style="uiStyle"
            @mouseleave="onMouseLeave">
            <div class="popover-arrow" ref="arrow" :style="uiArrowStyle"></div>
            <template v-if="config.i18n">
                <div class="f fv fa-l" :class="[config.help ? 'sp-r-xl' : '']">
                    <span v-if="config.i18n.badge"
                        class="el-badge el--t-default sm-b-xs"
                        v-html="$options.filters.athT(config.i18n.badge)"></span>
                    <h6 class="sm-b-s te-he" v-if="config.i18n.title" v-html="$options.filters.athT(config.i18n.title)">
                    </h6>
                </div>
                <p class="te-be" v-if="config.i18n.lbl" v-html="$options.filters.athT(config.i18n.lbl)"></p>
            </template>
            <ath-help-link v-if="config.help" :src="config.help" class="pa-tr sm-t-s sm-r-s" />
            <component v-if="config.comp !== false"
                class="popover-content"
                :is="config.comp"
                :popoverIndex="popoverIndex"
                v-bind="config.props"
                @actClose="close()" />
        </article>
    </aside>
</template>
<script>
import { guuid, isFunction } from '@aretheyhappy/utils';
import { computePosition, flip, shift, arrow, offset } from '@floating-ui/dom';
import UIMobile from '../../mixins/UIMobile';
import MoveDirective from '../../directives/Move';

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea:not([disabled]), '
    + 'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function relativeOffset(el) {
    const viewport_offset = el.getBoundingClientRect();
    return { x: viewport_offset.left, y: viewport_offset.top };
}

function absoluteOffset(el) {
    const offsetPos = { x: 0, y: 0 };

    let cursor = el;

    while (cursor) {
        offsetPos.x += cursor.offsetLeft - cursor.scrollLeft + cursor.clientLeft;
        offsetPos.y += cursor.offsetTop - cursor.scrollTop + cursor.clientTop;
        cursor = cursor.offsetParent;
    }

    return offsetPos;
}

//  Calculate the quadrant (https://en.wikipedia.org/wiki/Unit_circle) that an element is in
function viewportQuadrant(el, absolute = false) {
    const { x, y } = absolute ? absoluteOffset(el) : relativeOffset(el);
    const { width, height } = el.getBoundingClientRect();
    const viewport_width = Math.max(document.documentElement.clientWidth);
    const viewport_height = Math.max(document.documentElement.clientHeight);

    const element_x_pos = (x + (width * 0.5)) / viewport_width;
    const element_y_pos = (y + (height * 0.5)) / viewport_height;

    if (element_x_pos < 0.5 && element_y_pos < 0.5) {
        return 4;
    } else if (element_x_pos >= 0.5 && element_y_pos < 0.5) {
        return 1;
    } else if (element_x_pos >= 0.5 && element_y_pos >= 0.5) {
        return 2;
    } else if (element_x_pos < 0.5 && element_y_pos >= 0.5) {
        return 3;
    } else {
        return 2;
    }
}

export default {
    name: 'mead-global-popover-container',
    mixins: [UIMobile],
    props: {
        /**
         * Unique index identifying this popover instance, used to scope the
         * Flash open/close events it subscribes to and to determine parent/child
         * relationships between nested popovers when deciding whether to close on outside clicks.
         */
        popoverIndex: { type: String, default: '0' },
        /**
         * Whether this popover instance was spawned and is being driven by an external
         * controller rather than opening itself via Flash events. When true, the popover
         * opens immediately on mount using startupPopoverConfig and delegates closing to the controller.
         */
        isControllerSpawned: {type: Boolean, default: false},
        /**
         * The PopoverConfig to open with immediately on mount, used only when
         * isControllerSpawned is true.
         */
        startupPopoverConfig: {type: Object, default: () => ({})}
    },
    data() {
        return {
            uid: guuid(),
            // PopoverConfig
            config: false,
            // Element that is asking for the popover
            popover_trigger: false,
            meta: {
                bounds: false,    //  Will contain the initial dimensional bounds of the popover
            },
            // computed values from floating-ui/dom
            placement: {
                left: 0,
                top: 0,
                placement: '',
                arrow: { x: 0, y: 0 }
            },
            // Element focused before this popover opened, restored to when it closes
            previously_focused_element: false,
        };
    },
    methods: {
        /**
         * Elements within the popover that can receive keyboard focus
         */
        getFocusableEls: function () {
            if (!this.$refs.popover) return [];

            return Array.from(this.$refs.popover.querySelectorAll(FOCUSABLE_SELECTOR))
                .filter(el => el.offsetParent !== null);
        },
        /**
         * Open a new popover
         * @param {PopoverConfig} config
         */
        open: function (config) {
            // Get origin event's target element, do nothing if there's no origin
            const popover_trigger = config.target || (config.event || {}).currentTarget || false;
            if (!popover_trigger) return;

            // If the trigger element is the same as current trigger, set current trigger to false and return
            if (this.popover_trigger && this.popover_trigger === popover_trigger) {
                this.popover_trigger = false;
                return;
            }

            // Configure meta
            this.popover_trigger = popover_trigger;
            this.config = config;

            // Prevent event from escaping
            MeadCommon.DomUtils.preventImmediateEventEscape(config.event.originalEvent);

            // If on mobile, remove moveable property
            if (this.UIMobile) config.moveable = false;

            // Set new position
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    this.setPosition(config.quadrant || false, config.section || false, config.absolute);
                });
            });

            // Popovers rendering an actual component (as opposed to a plain i18n tooltip)
            // are interactive, so move keyboard focus into them like any other panel.
            if (config.comp !== false) {
                this.previously_focused_element = document.activeElement;
                this.$nextTick(() => {
                    const focusable = this.getFocusableEls();
                    (focusable[0] || this.$refs.popover).focus();
                });
            }

            // Add an event listener so we know when to close
            document.addEventListener('click', this.close, true);
        },
        /**
         * Close the popover
         * @param event
         */
        close: function (event = false) {
            //  Do nothing if this came from inside the popup
            if (event && event.target && this.$el.contains(event.target)) return;

            // Get all popovers
            const popovers = Array.from(document.querySelectorAll('.el-popover'));

            // Find which popover (if any) the click happened in
            let clickedPopoverIndex = null;
            popovers.forEach(popover => {
                if (popover.contains(event.target)) {
                    const match = Array.from(popover.classList).find(c => c.startsWith('el-popoverIndex-'));
                    if (match) {
                        const index = parseInt(match.replace('el-popoverIndex-', ''));
                        clickedPopoverIndex = index;
                    }
                }
            });

            // If clicked inside this popover or a descendant (same or higher index), do
            // nothing. Compared as numbers (popoverIndex is a String prop) and against
            // `null` specifically - 0 is a valid, found index, not "not found".
            if (clickedPopoverIndex !== null && clickedPopoverIndex >= parseInt(this.popoverIndex)) return;

            // Otherwise (clicked on parent popover or outside), close
            if (event && this.config.stop_propagation_on_close) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (isFunction(this.config.next)) {
                this.config.next();
            }

            this.popover_trigger = false;
            this.config = false;

            //  Remove event listener
            document.removeEventListener('click', this.close, true);

            // Remove free move setting
            MoveDirective.unbind(this.$refs.popover);

            // Restore focus to whatever had it before this popover opened
            if (this.previously_focused_element && this.previously_focused_element.focus) {
                this.previously_focused_element.focus();
            }
            this.previously_focused_element = false;

            MeadCommon.Flash.publish(MeadCommon.PopoverServiceEvents.POPOVER_CLOSED_INTERNAL);
        },
        /**
         * Request Controller to close this popover
         */
        closeWithController: function () {
            this.$emit('onClose');
        },
        /**
         * Keyboard event handler, if event key is escape close modal
         * @param event
         */
        onEscHandler: function (event) {
            if (event.keyCode !== 27 || !this.config || !this.config.esckill) return;
            document.removeEventListener('keyup', this.onEscHandler);
            this.close(false);
        },
        /**
         * Reposition the popover on scroll
         */
        onScroll: function () {
            if (this.config.v2 && !this.$refs.popover.classList.contains('ath-moved')) {
                this.setPosition();
            }
        },
        /**
         * Reposition element based on target
         * @param predetermined_quadrant
         * @param predetermined_section
         * @param absolute
         */
        setPosition: function (predetermined_quadrant = false, predetermined_section = false, absolute = true) {
            if (this.config.v2) {
                computePosition(this.popover_trigger, this.$refs.popover, {
                    placement: this.config.placement || 'bottom-start',
                    strategy: 'fixed',
                    middleware: [
                        // Set an offset from the target element
                        offset(12),
                        // Uses opposite placement value if the popover cannot fit in the desired one
                        flip(),
                        // Adds padding to the popover placement
                        shift({ padding: 10 }),
                        // Positions the arrow for us
                        arrow({ element: this.$refs.arrow })],
                }).then(({ x, y, placement, middlewareData }) => {
                    this.placement.left = x;
                    this.placement.top = y;
                    this.placement.placement = placement;
                    this.placement.arrow = middlewareData.arrow;
                });
                return;
            }

            const quadrant = predetermined_quadrant || viewportQuadrant(this.popover_trigger);
            const bounds = this.popover_trigger.getBoundingClientRect();
            const offsetPos = absolute
                ? absoluteOffset(this.popover_trigger)
                : relativeOffset(this.popover_trigger);

            //  Calculate position of an element based on the quadrant and its bounds
            const { width, height } = bounds;
            const { x, y } = offsetPos;
            const { clientWidth, clientHeight } = document.documentElement;

            let pos = false;
            switch (quadrant) {
                case 1:
                    pos = { top: y + height, right: clientWidth - x - width };
                    break;
                case 2:
                    pos = { bottom: clientHeight - y, right: clientWidth - x };
                    break;
                case 3:
                    pos = { bottom: clientHeight - y, left: x + width };
                    break;
                case 4:
                    pos = { top: y + height, left: x + width };
                    break;
                default:
                    break;
            }

            //  If position is false, something went wrong, don't do anything
            if (!pos) return;

            //  Override position if a section was provided
            if (predetermined_section !== false) {
                switch (predetermined_section) {
                    case 'left':
                        Object.assign(pos, { right: clientWidth - x, left: false });
                        break;
                    case 'right':
                        Object.assign(pos, { left: x + width, right: false });
                        break;
                    case 'top':
                        Object.assign(pos, { bottom: clientHeight - y, top: false });
                        break;
                    case 'bottom':
                        Object.assign(pos, { top: y + height, bottom: false });
                        break;
                    default:
                        break;
                }
            }

            this.meta.bounds = pos;
        },
        onMouseLeave: function () {
            if (this.config.hide_on_mouse_leave) {
                if (this.isControllerSpawned) this.closeWithController();
                else this.close();
            }
        }
    },
    computed: {
        uiArrowStyle: function () {
            const staticSide = {
                top: 'bottom',
                right: 'left',
                bottom: 'top',
                left: 'right',
            }[this.placement.placement.split('-')[0]];

            // Since we are rendering a square at 45 degress clockwise that is
            // overtop of the popover. We only want to show borders on the sides over
            // the edge.
            const leftBorder = staticSide === 'top' || staticSide === 'left';
            const rightBorder = staticSide === 'bottom' || staticSide === 'right';
            const topBorder = staticSide === 'top' || staticSide === 'right';
            const bottomBorder = staticSide === 'bottom' || staticSide === 'left';

            return Object.assign({
                left: this.placement.arrow.x != null ? `${this.placement.arrow.x}px` : '',
                top: this.placement.arrow.y != null ? `${this.placement.arrow.y}px` : '',
                right: '',
                bottom: '',
                'border-left': leftBorder ? '1px solid var(--c-panel-border)' : 'none',
                'border-right': rightBorder ? '1px solid var(--c-panel-border)' : 'none',
                'border-top': topBorder ? '1px solid var(--c-panel-border)' : 'none',
                'border-bottom': bottomBorder ? '1px solid var(--c-panel-border)' : 'none',
                [staticSide]: '-10px',
            }, !this.config.v2 || !this.config.show_arrow ? {
                display: 'none'
            } : {} );
        },
        uiStyle: function () {
            if (this.config.v2) {
                return {
                    left: `${this.placement.left}px`,
                    top: `${this.placement.top}px`,
                };
            }
            return {
                'top': this.meta.bounds.top ? (this.meta.bounds.top + 'px') : 'initial',
                'right': this.meta.bounds.right ? (this.meta.bounds.right + 'px') : 'initial',
                'bottom': this.meta.bounds.bottom ? (this.meta.bounds.bottom + 'px') : 'initial',
                'left': this.meta.bounds.left ? (this.meta.bounds.left + 'px') : 'initial',
            };
        }
    },
    mounted() {
        if (this.isControllerSpawned) {
            this.open(this.startupPopoverConfig);
        }
    },
    created() {
        if (!this.isControllerSpawned) {
            MeadCommon.Flash.subscribe(this.uid, `global-popover:open:${this.popoverIndex}`, this.open);
            MeadCommon.Flash.subscribe(this.uid, `global-popover:close:${this.popoverIndex}`, this.close);
        }
        document.addEventListener('keyup', this.onEscHandler, true);
        document.addEventListener('scroll' , this.onScroll, true);
    },
    destroyed() {
        MeadCommon.Flash.unsubscribe(this.uid);
        document.removeEventListener('keyup', this.onEscHandler, true);
        document.removeEventListener('scroll' , this.onScroll, true);
        document.removeEventListener('click', this.close, true);
    },
};
</script>
<style lang="scss" scoped>
@import '../../../sass/_variables.scss';

.el--focus {
    &:before {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        content: '';
        background-color: var(--c-overlay-bg);
        opacity: .8;
        pointer-events: none;
        z-index: $zx-popover - 1;
        animation: overlayBgIn .2s forwards ease-out;
    }

    .el-popover {
        border: none;
        box-shadow: none;
    }
}

.el-popover {
    position: fixed;
    border-radius: $br-m;
    border: 1px solid var(--c-panel-border);
    box-shadow: var(--c-shade-l);
    background-color: var(--c-panel-bg);
    z-index: $zx-popover;
    animation: popoverIn .2s forwards ease-out;

    &:focus-visible {
        outline: 2px solid var(--c-panel-border-active);
        outline-offset: -2px;
    }

    .popover-arrow {
        position: absolute;
        background: var(--c-panel-bg);
        width: 20px;
        height: 20px;
        transform: rotate(45deg);
        z-index: $zx-popover;
        border-color: var(--c-panel-border);
    }

    .popover-content {
        // Needed to make sure the popover arrow is rendered under the popover content
        z-index: $zx-popover;
    }

    // Class is added by the move directive.
    // Hide the popover arrow when the popover is moved around
    &.ath-moved {
        .popover-arrow {
            display: none;
        }
    }

    h6 {
        font-size: $fs-m;
    }

    p {
        font-size: $fs-s;
    }

    &.el--t-tooltip {
        max-width: 35rem;
    }

    @media screen and (max-width: $mq-ds) {
        &:not(.el--popover--mfull) {
            left: $s-m !important;
            right: $s-m !important;
            max-width: calc(100% - (#{$s-m} + #{$s-m}));
        }

        &.el-popover--mfull {
            position: fixed;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            width: 100%;
            min-width: 100%;
            height: 100%;
            border-radius: 0;
            border: none;
        }
    }
}
</style>
